/**
 * API Client for Olympic Fit Wellness
 * Complete API integration with Supabase backend
 */
import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

class ApiClient {
  constructor() {
    this.baseUrl = API_URL?.replace('http://', 'https://') || '';
    this.tokenCache = null;
    this.tokenExpiry = null;
  }

  async getAuthToken() {
    try {
      // Check if we have a valid cached token
      if (this.tokenCache && this.tokenExpiry && Date.now() < this.tokenExpiry) {
        return this.tokenCache;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        this.tokenCache = session.access_token;
        // Cache for 5 minutes less than actual expiry to be safe
        this.tokenExpiry = session.expires_at ? (session.expires_at * 1000) - 300000 : Date.now() + 3300000;
        return session.access_token;
      }
      return null;
    } catch {
      this.tokenCache = null;
      this.tokenExpiry = null;
      return null;
    }
  }

  clearTokenCache() {
    this.tokenCache = null;
    this.tokenExpiry = null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}/api${endpoint}`;
    
    // Get auth token
    const token = await this.getAuthToken();
    
    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add Authorization header if token exists
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      config.body = JSON.stringify(options.body);
    } else if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
      config.body = options.body;
    }

    try {
      const response = await fetch(url, config);
      
      // If we get a 401, clear token cache and try once more
      if (response.status === 401 && token) {
        this.clearTokenCache();
        const newToken = await this.getAuthToken();
        if (newToken && newToken !== token) {
          config.headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, config);
          if (!retryResponse.ok) {
            const error = await retryResponse.json().catch(() => ({ detail: 'An error occurred' }));
            throw new Error(error.detail || `Request failed with status ${retryResponse.status}`);
          }
          return retryResponse.json();
        }
        // Token refresh didn't help, throw original 401 error
        throw new Error('Unauthorized - please log in again');
      }
      
      if (!response.ok) {
        // Read error body directly - don't clone
        const errorText = await response.text();
        let errorDetail = `Request failed with status ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorDetail = errorJson.detail || errorDetail;
        } catch {
          // Not JSON, use text or default
          if (errorText) errorDetail = errorText;
        }
        throw new Error(errorDetail);
      }

      return response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // =============================================
  // AUTH
  // =============================================
  async getMe() {
    return this.request('/auth/me');
  }

  async logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword }
    });
  }

  async enable2FA() {
    return this.request('/auth/enable-2fa', { method: 'POST' });
  }

  // =============================================
  // LOCATIONS
  // =============================================
  async getLocations(activeOnly = true) {
    return this.request(`/locations?active_only=${activeOnly}`);
  }

  async getLocation(locationId) {
    return this.request(`/locations/${locationId}`);
  }

  // =============================================
  // CLASSES
  // =============================================
  async getClasses(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/classes${query ? `?${query}` : ''}`);
  }

  async getClass(classId) {
    return this.request(`/classes/${classId}`);
  }

  async getClassCategories() {
    return this.request('/classes/categories');
  }

  // =============================================
  // SCHEDULE (CLASS INSTANCES)
  // =============================================
  async getSchedule(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/schedule${query ? `?${query}` : ''}`);
  }

  async createClassInstance(data) {
    return this.request('/schedule', { method: 'POST', body: data });
  }

  // =============================================
  // MEMBERSHIPS
  // =============================================
  async getMembershipTiers() {
    return this.request('/membership/tiers');
  }

  async getMembershipTier(tier) {
    return this.request(`/membership/tiers/${tier}`);
  }

  // =============================================
  // WORKOUTS (Member Portal)
  // =============================================
  async getMyWorkouts(limit = 20) {
    return this.request(`/member/workouts?limit=${limit}`);
  }

  async logWorkout(workout) {
    return this.request('/member/workouts', {
      method: 'POST',
      body: workout
    });
  }

  async getWorkoutStats() {
    return this.request('/member/workout-stats');
  }

  // =============================================
  // NUTRITION (Member Portal)
  // =============================================
  async getMyMeals(date = null) {
    const query = date ? `?date=${date}` : '';
    return this.request(`/member/meals${query}`);
  }

  async logMeal(meal) {
    return this.request('/member/meals', {
      method: 'POST',
      body: meal
    });
  }

  async getNutritionStats(date = null) {
    const query = date ? `?date=${date}` : '';
    return this.request(`/member/nutrition-stats${query}`);
  }

  // =============================================
  // BOOKINGS (Member Portal)
  // =============================================
  async getMyBookings() {
    return this.request('/bookings');
  }

  async createBooking(classInstanceId) {
    return this.request('/bookings', {
      method: 'POST',
      body: { class_instance_id: classInstanceId }
    });
  }

  async cancelBooking(bookingId) {
    return this.request(`/bookings/${bookingId}`, { method: 'DELETE' });
  }

  // =============================================
  // MEMBER PROFILE
  // =============================================
  async getMyProfile() {
    return this.request('/member/profile');
  }

  async updateMyProfile(data) {
    return this.request('/member/profile', { method: 'PUT', body: data });
  }

  async getMyMembership() {
    return this.request('/member/membership');
  }

  // =============================================
  // CONTACT
  // =============================================
  async submitContactForm(data) {
    return this.request('/contact', { method: 'POST', body: data });
  }

  async subscribeNewsletter(email, name = null) {
    return this.request('/newsletter', {
      method: 'POST',
      body: { email, name },
    });
  }

  // =============================================
  // TRAINERS
  // =============================================
  async getTrainers() {
    return this.request('/trainers');
  }

  // =============================================
  // CMS (Admin)
  // =============================================
  async getCMSPages() {
    return this.request('/cms/pages');
  }

  async getCMSPage(slug) {
    return this.request(`/cms/pages/${slug}`);
  }

  async updateCMSPage(slug, data) {
    return this.request(`/cms/pages/${slug}`, { method: 'PUT', body: data });
  }

  async createCMSPage(title, slug, content = {}) {
    return this.request(`/cms/pages?title=${encodeURIComponent(title)}&slug=${encodeURIComponent(slug)}`, {
      method: 'POST',
      body: content
    });
  }

  // =============================================
  // ADMIN - USERS
  // =============================================
  async getAdminUsers() {
    return this.request('/admin/users');
  }

  async updateUserRole(userId, role) {
    return this.request(`/admin/users/${userId}/role?role=${encodeURIComponent(role)}`, {
      method: 'PUT'
    });
  }

  async createUser(userData) {
    return this.request('/admin/users', {
      method: 'POST',
      body: userData
    });
  }

  async suspendUser(userId, reason = null) {
    return this.request(`/admin/users/${userId}/suspend`, {
      method: 'PUT',
      body: { reason }
    });
  }

  async activateUser(userId) {
    return this.request(`/admin/users/${userId}/activate`, {
      method: 'PUT'
    });
  }

  async deleteUser(userId) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  }

  // =============================================
  // ADMIN - SITE SETTINGS
  // =============================================
  async getSiteSettings() {
    return this.request('/admin/settings');
  }

  async updateSiteSettings(settings) {
    return this.request('/admin/settings', {
      method: 'PUT',
      body: settings
    });
  }

  // =============================================
  // ADMIN - CONTACTS
  // =============================================
  async getAdminContacts() {
    return this.request('/admin/contacts');
  }

  async updateContactStatus(submissionId, status) {
    return this.request(`/admin/contacts/${submissionId}?status=${encodeURIComponent(status)}`, {
      method: 'PUT'
    });
  }

  // =============================================
  // ADMIN - INTEGRATIONS
  // =============================================
  async getIntegrationCategories() {
    return this.request('/admin/integrations/categories');
  }

  async getAvailableIntegrations() {
    return this.request('/admin/integrations/available');
  }

  async getConfiguredIntegrations() {
    return this.request('/admin/integrations/configured');
  }

  async getConfiguredIntegration(name) {
    return this.request(`/admin/integrations/${name}`);
  }

  async configureIntegration(name, credentials) {
    return this.request(`/admin/integrations/${name}/configure`, {
      method: 'POST',
      body: { credentials }
    });
  }

  async testIntegration(name) {
    return this.request(`/admin/integrations/${name}/test`, {
      method: 'POST'
    });
  }

  async toggleIntegration(name) {
    return this.request(`/admin/integrations/${name}/toggle`, {
      method: 'POST'
    });
  }

  // =============================================
  // ADMIN - NEWSLETTER
  // =============================================
  async getAdminNewsletter() {
    return this.request('/admin/newsletter');
  }

  // =============================================
  // ADMIN - STATS
  // =============================================
  async getAdminStats() {
    return this.request('/admin/stats');
  }

  // =============================================
  // ADMIN - RECENT ACTIVITY
  // =============================================
  async getRecentActivity() {
    return this.request('/subscriptions/recent-activity');
  }

  // =============================================
  // SUBSCRIPTIONS - MEMBER
  // =============================================
  async getMySubscription() {
    return this.request('/subscriptions/my-subscription');
  }

  async getPaymentHistory() {
    return this.request('/subscriptions/payment-history');
  }

  async cancelSubscription(reason = null) {
    return this.request('/subscriptions/cancel', {
      method: 'POST',
      body: { reason }
    });
  }

  async reactivateSubscription() {
    return this.request('/subscriptions/reactivate', {
      method: 'POST'
    });
  }

  async upgradeSubscription(packageId, originUrl) {
    return this.request('/subscriptions/upgrade', {
      method: 'POST',
      body: { package_id: packageId, origin_url: originUrl }
    });
  }

  // =============================================
  // ADMIN - LOCATIONS
  // =============================================
  async createLocation(data) {
    const { name, address, city, state, zip_code } = data;
    return this.request(`/admin/locations?name=${encodeURIComponent(name)}&address=${encodeURIComponent(address)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&zip_code=${encodeURIComponent(zip_code)}`, {
      method: 'POST'
    });
  }

  async updateLocation(locationId, data) {
    return this.request(`/admin/locations/${locationId}`, {
      method: 'PUT',
      body: data
    });
  }

  // =============================================
  // STAFF - BOOKINGS
  // =============================================
  async getStaffBookings(date = null) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/staff/bookings${params}`);
  }

  async updateBookingStatus(bookingId, status) {
    return this.request(`/staff/bookings/${bookingId}/status?status=${encodeURIComponent(status)}`, {
      method: 'PUT'
    });
  }

  // =============================================
  // TRAINER PROFILES
  // =============================================
  async getTrainerProfiles() {
    return this.request('/trainers/profiles');
  }

  // =============================================
  // SEED DATA (Admin)
  // =============================================
  async seedData() {
    return this.request('/admin/seed-data', { method: 'POST' });
  }

  // =============================================
  // IMAGE UPLOAD (Admin)
  // =============================================
  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/images/upload', {
      method: 'POST',
      body: formData
    });
  }

  async getImages() {
    return this.request('/images');
  }

  async deleteImage(filename) {
    return this.request(`/images/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  }

  // =============================================
  // CALENDAR & BOOKING SYSTEM
  // =============================================
  
  // Specializations & Categories
  async getSpecializations() {
    return this.request('/calendar/specializations');
  }

  async getServiceCategories() {
    return this.request('/calendar/service-categories');
  }

  // Sessions
  async getCalendarSessions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/calendar/sessions${query ? `?${query}` : ''}`);
  }

  async getCalendarSession(sessionId) {
    return this.request(`/calendar/sessions/${sessionId}`);
  }

  async createCalendarSession(sessionData) {
    return this.request('/calendar/sessions', {
      method: 'POST',
      body: sessionData
    });
  }

  async updateCalendarSession(sessionId, updateData) {
    return this.request(`/calendar/sessions/${sessionId}`, {
      method: 'PUT',
      body: updateData
    });
  }

  async cancelCalendarSession(sessionId, reason = null) {
    return this.request(`/calendar/sessions/${sessionId}?reason=${encodeURIComponent(reason || '')}`, {
      method: 'DELETE'
    });
  }

  // Bookings
  async getMyCalendarBookings(status = null) {
    const query = status ? `?status=${status}` : '';
    return this.request(`/calendar/bookings${query}`);
  }

  async getStaffBookings(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/calendar/bookings/staff${query ? `?${query}` : ''}`);
  }

  async getPendingBookingRequests() {
    return this.request('/calendar/bookings/pending-requests');
  }

  async createCalendarBooking(sessionId, requestMessage = null, isRequest = false) {
    return this.request('/calendar/bookings', {
      method: 'POST',
      body: { 
        session_id: sessionId, 
        request_message: requestMessage,
        is_request: isRequest
      }
    });
  }

  async respondToBooking(bookingId, status, responseMessage = null) {
    return this.request(`/calendar/bookings/${bookingId}/respond`, {
      method: 'PUT',
      body: { status, response_message: responseMessage }
    });
  }

  async cancelCalendarBooking(bookingId, reason = null, requestRefund = false) {
    return this.request(`/calendar/bookings/${bookingId}/cancel`, {
      method: 'PUT',
      body: { reason, request_refund: requestRefund }
    });
  }

  // Staff Availability
  async getStaffAvailability(staffId) {
    return this.request(`/calendar/availability/${staffId}`);
  }

  async getMyAvailability() {
    return this.request('/calendar/my-availability');
  }

  async setMyAvailability(slots) {
    return this.request('/calendar/my-availability', {
      method: 'POST',
      body: slots
    });
  }

  // Bookable Staff
  async getBookableStaff() {
    return this.request('/calendar/staff');
  }

  // Session Allowance
  async getMyAllowance() {
    return this.request('/calendar/my-allowance');
  }

  async setMemberAllowance(memberId, totalAllowed, notes = null) {
    return this.request(`/calendar/allowance/${memberId}?total_allowed=${totalAllowed}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`, {
      method: 'POST'
    });
  }
}

export const api = new ApiClient();
export default api;
